import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";

export interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  autoFocus?: boolean;
  error?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}

export interface OtpInputHandle {
  focus: () => void;
  clear: () => void;
}

export const OtpInput = forwardRef<OtpInputHandle, OtpInputProps>(function OtpInput(
  {
    length = 8,
    value,
    onChange,
    onComplete,
    autoFocus,
    error,
    disabled,
    ariaLabel = "One-time code",
  },
  ref,
) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [internalLen] = useState(length);

  const cells = useMemo(() => {
    const out = new Array(internalLen).fill("");
    for (let i = 0; i < Math.min(internalLen, value.length); i++) {
      out[i] = /\d/.test(value[i]!) ? value[i] : "";
    }
    return out as string[];
  }, [internalLen, value]);

  useImperativeHandle(ref, () => ({
    focus: () => inputsRef.current[0]?.focus(),
    clear: () => onChange(""),
  }));

  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus();
  }, [autoFocus]);

  const writeAt = useCallback(
    (idx: number, digit: string): void => {
      const next = cells.slice();
      next[idx] = digit;
      onChange(next.join(""));
    },
    [cells, onChange],
  );

  const handleChange = (idx: number, raw: string): void => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      // The user typed a non-digit; ignore (cell stays as-is).
      return;
    }
    if (digits.length === 1) {
      writeAt(idx, digits);
      const isLast = idx === internalLen - 1;
      if (!isLast) inputsRef.current[idx + 1]?.focus();
      else {
        const filled = (cells.slice(0, idx).join("") + digits).padEnd(internalLen, "");
        if (filled.length === internalLen && /^\d+$/.test(filled)) {
          onComplete?.(filled);
        }
      }
      return;
    }
    // Multi-char (e.g. autofill or fast paste landing in an input). Spread.
    spreadFrom(idx, digits);
  };

  const spreadFrom = (startIdx: number, digits: string): void => {
    const next = cells.slice();
    let i = startIdx;
    let consumed = 0;
    for (const d of digits) {
      if (i >= internalLen) break;
      next[i] = d;
      i++;
      consumed++;
    }
    onChange(next.join(""));
    const focusIdx = Math.min(startIdx + consumed, internalLen - 1);
    inputsRef.current[focusIdx]?.focus();
    inputsRef.current[focusIdx]?.select();

    const merged = next.join("");
    if (merged.length === internalLen && /^\d+$/.test(merged)) {
      onComplete?.(merged);
    }
  };

  const handleKeyDown = (idx: number, e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Backspace") {
      if (cells[idx]) {
        writeAt(idx, "");
        return;
      }
      // Empty cell: step back to the previous one and clear it.
      if (idx > 0) {
        e.preventDefault();
        writeAt(idx - 1, "");
        inputsRef.current[idx - 1]?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      inputsRef.current[idx - 1]?.focus();
      return;
    }
    if (e.key === "ArrowRight" && idx < internalLen - 1) {
      e.preventDefault();
      inputsRef.current[idx + 1]?.focus();
      return;
    }
  };

  const handlePaste = (idx: number, e: ClipboardEvent<HTMLInputElement>): void => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    spreadFrom(idx, pasted);
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex items-center justify-between gap-2"
    >
      {cells.map((cell, idx) => (
        <input
          key={idx}
          ref={(el) => {
            inputsRef.current[idx] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={idx === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={cell}
          disabled={disabled}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={(e) => handlePaste(idx, e)}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={`${ariaLabel} digit ${idx + 1}`}
          className={
            "w-10 h-12 sm:w-11 sm:h-12 text-center text-lg font-semibold rounded-md border bg-white outline-none transition-colors " +
            (error
              ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200"
              : "border-gray-300 focus:border-penn-500 focus:ring-2 focus:ring-penn-200") +
            (disabled ? " bg-gray-50 text-gray-400 cursor-not-allowed" : "")
          }
        />
      ))}
    </div>
  );
});
